/* ----------------- React --------------- */
import type React from "react";
import { forwardRef } from "react";

/* ----------------- React Native --------------- */
import { ScrollView, type ScrollViewProps, type ViewStyle } from "react-native";

/* ----------------- Components --------------- */
import Container, { type ContainerProps } from "../Container";

import { isWeb } from "@memorang/helpers/platform";
/* ----------------- Types --------------- */
import type { AlignItems } from "../Box";

type Props = {
	children: React.ReactNode;
	maxWidth?: "xxs" | "xs" | "sm" | "md" | "lg";
	alignItems?: AlignItems;
	containerProps?: Omit<ContainerProps, "children">;
	/** Reserve scrollbar space for consistent layout (web only). */
	stableScrollbarGutter?: boolean | "both-edges";
} & ScrollViewProps;

const STABLE_SCROLLBAR_GUTTER_STYLE = {
	scrollbarGutter: "stable",
} as unknown as ViewStyle;

const STABLE_BOTH_EDGES_SCROLLBAR_GUTTER_STYLE = {
	scrollbarGutter: "stable both-edges",
} as unknown as ViewStyle;

const ScrollContainer = forwardRef<ScrollView, Props>(
	(
		{
			children,
			containerProps,
			maxWidth,
			alignItems,
			contentContainerStyle,
			stableScrollbarGutter,
			style,
			...rest
		},
		ref,
	) => {
		return (
			<ScrollView
				ref={ref}
				showsVerticalScrollIndicator={isWeb}
				contentContainerStyle={[{ flexGrow: 1 }, contentContainerStyle]}
				style={[
					isWeb &&
						stableScrollbarGutter &&
						(stableScrollbarGutter === "both-edges"
							? STABLE_BOTH_EDGES_SCROLLBAR_GUTTER_STYLE
							: STABLE_SCROLLBAR_GUTTER_STYLE),
					style,
				]}
				{...rest}
			>
				<Container
					alignItems={alignItems}
					maxWidth={maxWidth || "md"}
					{...(containerProps || {})}
				>
					{children}
				</Container>
			</ScrollView>
		);
	},
);

export default ScrollContainer;
